import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, ChevronRightIcon, PlaceIcon } from '../../components/ui/icons';
import { listChildren, reorderLocations, type LocationNode } from './api';

interface BranchProps {
  parentId: string | null;
  selectedId: string | null;
  reordering: boolean;
  onSelect: (id: string) => void;
}

/**
 * One level of the tree, loaded when its parent is expanded.
 *
 * Lazy rather than fetching the whole tree: a workshop has hundreds of nodes,
 * and the levels below a collapsed cabinet are work nobody asked for.
 */
function Branch({ parentId, selectedId, reordering, onSelect }: BranchProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['locations', 'children', parentId],
    queryFn: () => listChildren(parentId),
  });

  // Local copy so the list follows the pointer during a drag. Server order wins
  // whenever it changes underneath, otherwise a rejected reorder would leave
  // the screen showing an arrangement the database never accepted.
  const [order, setOrder] = useState<LocationNode[]>([]);
  const [dragging, setDragging] = useState<string | null>(null);

  useEffect(() => {
    if (data) setOrder(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (ids: string[]) => reorderLocations(parentId, ids),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['locations', 'children', parentId] }),
  });

  function moveOver(targetId: string) {
    if (!dragging || dragging === targetId) return;
    setOrder((current) => {
      const from = current.findIndex((node) => node.id === dragging);
      const to = current.findIndex((node) => node.id === targetId);
      if (from < 0 || to < 0) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  if (isLoading) return <p className="tree__hint">{t('common.loading')}</p>;
  if (isError) return <p className="tree__hint tree__hint--error">{t('locations.loadFailed')}</p>;
  if (!order.length) {
    return parentId ? null : <p className="tree__hint">{t('locations.empty')}</p>;
  }

  return (
    <ul className="tree__list">
      {order.map((node) => (
        <TreeItem
          key={node.id}
          node={node}
          selectedId={selectedId}
          reordering={reordering}
          dragging={dragging === node.id}
          onSelect={onSelect}
          onDragStart={() => setDragging(node.id)}
          onDragOver={() => moveOver(node.id)}
          onDrop={() => {
            setDragging(null);
            save.mutate(order.map((item) => item.id));
          }}
        />
      ))}
    </ul>
  );
}

interface ItemProps {
  node: LocationNode;
  selectedId: string | null;
  reordering: boolean;
  dragging: boolean;
  onSelect: (id: string) => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
}

function TreeItem({
  node,
  selectedId,
  reordering,
  dragging,
  onSelect,
  onDragStart,
  onDragOver,
  onDrop,
}: ItemProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isSelected = node.id === selectedId;
  const name = useRef<HTMLSpanElement>(null);

  // The name, not the row. A row runs the full width of the tree — that is what
  // gives every level the same background — so it is never out of view
  // sideways, and scrolling it into view did nothing when the name itself had
  // been left off the left edge. Choosing a deep place scrolls right; choosing
  // its parent then has to scroll back, and only the name knows that.
  useEffect(() => {
    if (isSelected) name.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [isSelected]);

  return (
    <li
      className="tree__item"
      draggable={reordering}
      // Every handler stops propagation. A nested row sits inside its parent's
      // <li>, so without this a drag over a shelf would also register on the
      // cabinet containing it, and two levels would try to reorder at once.
      onDragStart={(event) => {
        event.stopPropagation();
        // Firefox refuses to start a drag without payload on the transfer.
        event.dataTransfer.setData('text/plain', node.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragOver={(event) => {
        if (!reordering) return;
        event.stopPropagation();
        // Without preventDefault the element is not a drop target at all.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver();
      }}
      onDrop={(event) => {
        if (!reordering) return;
        event.stopPropagation();
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={(event) => {
        event.stopPropagation();
        onDrop();
      }}
    >
      <div
        className={[
          'tree__row',
          isSelected ? 'tree__row--selected' : '',
          reordering ? 'tree__row--draggable' : '',
          dragging ? 'tree__row--dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {reordering && (
          <span className="tree__grip" aria-hidden="true">
            ⠿
          </span>
        )}

        {/* The expander stays available while reordering: shelves are reordered
            inside their cabinet, which means opening it first. */}
        <button
          type="button"
          className="tree__toggle"
          onClick={() => setExpanded((value) => !value)}
          disabled={!node.has_children}
          aria-expanded={node.has_children ? expanded : undefined}
          aria-label={expanded ? t('locations.collapse') : t('locations.expand')}
        >
          {node.has_children && (expanded ? <ChevronDownIcon /> : <ChevronRightIcon />)}
        </button>

        <button
          type="button"
          className="tree__label"
          onClick={() => onSelect(node.id)}
          disabled={reordering}
        >
          {/* The shape of the row says "cabinet" or "box" before a word of it is
              read — which is the difference between scanning a tree and reading
              a hundred names set in the same type. */}
          <PlaceIcon name={node.kind} className="tree__icon" />
          <span className="tree__name" ref={name}>
            {node.name}
          </span>
          <span className="tree__kind">{t(`locations.kind.${node.kind}`)}</span>
        </button>
      </div>

      {expanded && (
        <div className="tree__children">
          <Branch
            parentId={node.id}
            selectedId={selectedId}
            reordering={reordering}
            onSelect={onSelect}
          />
        </div>
      )}
    </li>
  );
}

interface Props {
  selectedId: string | null;
  reordering: boolean;
  onSelect: (id: string) => void;
}

export function LocationTree({ selectedId, reordering, onSelect }: Props) {
  return (
    <div className="tree">
      <Branch
        parentId={null}
        selectedId={selectedId}
        reordering={reordering}
        onSelect={onSelect}
      />
    </div>
  );
}
